import { serviceCategory } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Service Category Service
 * Handles all service category master API calls.
 *
 * API Endpoints:
 * - GET /api/v1/admin/service-category/list - Get service category list
 * - GET /api/v1/admin/service-category/get - Get single service category
 * - POST /api/v1/admin/service-category/add - Add new service category
 * - PUT /api/v1/admin/service-category/update - Update service category
 * - PUT /api/v1/admin/service-category/update-status - Toggle status
 * - DELETE /api/v1/admin/service-category/delete - Delete service category
 */
class ServiceCategoryService {
  /**
   * @name getList
   * @description Fetches service category list from admin API with pagination, search and status filter.
   * @param {Object} params - Query parameters (page, limit, search, status)
   * @returns {Promise<Object>} The response data containing the service category list.
   */
  static async getList(params = {}) {
    try {
      const response = await axios.get(serviceCategory.list, { params });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name get
   * @description Fetches a single service category by UUID.
   * @param {string} uuid - Service Category UUID
   * @returns {Promise<Object>} The response data containing service category details.
   */
  static async get(uuid) {
    try {
      const response = await axios.get(serviceCategory.get, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name add
   * @description Creates a new service category.
   * @param {Object} data - Service category data with name and is_active
   * @returns {Promise<Object>} The response data.
   */
  static async add(data) {
    try {
      const response = await axios.post(serviceCategory.add, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name update
   * @description Updates an existing service category's name and/or status.
   * @param {Object} data - Service category data with uuid, name, and is_active
   * @returns {Promise<Object>} The response data.
   */
  static async update(data) {
    try {
      const response = await axios.put(serviceCategory.update, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name updateStatus
   * @description Toggles the active/inactive status of a service category.
   * @param {string} uuid - Service Category UUID
   * @param {boolean} isActive - New status
   * @returns {Promise<Object>} The response data.
   */
  static async updateStatus(uuid, isActive) {
    try {
      const response = await axios.put(serviceCategory.updateStatus, { uuid, is_active: isActive });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name delete
   * @description Deletes a service category by UUID.
   * @param {string} uuid - Service Category UUID
   * @returns {Promise<Object>} The response data.
   */
  static async delete(uuid) {
    try {
      const response = await axios.delete(serviceCategory.delete, { params: { uuid } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { ServiceCategoryService };
